class GzSellSearch extends HTMLElement {
  constructor() {
    super();
    this.brands = [];
    this.filteredBrands = [];
    this.isOpen = false;
    this.selectedValue = '';
    this.activeIndex = -1;
    this.apiUrl = window.GzTheme?.OMNI_URL || '';
    this.props = this.parseProps();
    this.debouncedAnnounce = debounce(() => this.announceResults(), 500);
  }

  connectedCallback() {
    this.pageUrl = this.getAttribute('data-pyd-page-url') || '';
    this.cacheElements();
    this.attachListeners();
    this.loadBrands();
  }

  parseProps() {
    try {
      const propsAttr = this.getAttribute('data-props');
      return propsAttr ? JSON.parse(propsAttr) : {};
    } catch (error) {
      console.error('Error parsing data-props:', error);
      return {};
    }
  }

  cacheElements() {
    this.input = this.querySelector('[data-input]');
    this.icon = this.querySelector('[data-icon]');
    this.spinner = this.querySelector('[data-spinner]');
    this.dropdown = this.querySelector('[dropdown-menu]');
    this.status = this.querySelector('[data-status]');
  }

  attachListeners() {
    // Open dropdown and clear input temporarily
    this.input?.addEventListener('focus', () => {
      this.input.value = '';
      this.showAllBrands();
      this.open();
    });
    
    this.input?.addEventListener('click', () => {
      this.input.value = '';
      this.showAllBrands();
      this.open();
    });

    // Filter while typing
    this.input?.addEventListener('input', (e) => {
      this.filter(e.target.value);
      this.open();
    });

    // Restore selected value if input is empty on blur
    this.input?.addEventListener('blur', () => {
      setTimeout(() => {
        if (!this.input.value.trim()) {
          this.input.value = this.selectedValue;
        }
      }, 200);
    });

    // Arrow-key navigation through the listbox (standard combobox pattern)
    this.input?.addEventListener('keydown', (e) => {
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter'].includes(e.key)) return;
      if (!this.isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        this.showAllBrands();
        this.open();
      }
      if (!this.isOpen) return;

      e.preventDefault();

      const options = this.getSelectableOptions();
      if (!options.length) return;

      if (e.key === 'ArrowDown') {
        this.setActiveIndex(this.activeIndex + 1 >= options.length ? 0 : this.activeIndex + 1);
      } else if (e.key === 'ArrowUp') {
        this.setActiveIndex(this.activeIndex - 1 < 0 ? options.length - 1 : this.activeIndex - 1);
      } else if (e.key === 'Home') {
        this.setActiveIndex(0);
      } else if (e.key === 'End') {
        this.setActiveIndex(options.length - 1);
      } else if (e.key === 'Enter') {
        options[this.activeIndex]?.click();
      }
    });

    // Handle brand selection
    this.dropdown?.addEventListener('click', (e) => {
      const option = e.target.closest('[data-brand-id]');
      const isHeader = e.target.closest('[data-is-header]');
      
      if (!option || isHeader) return;

      const name = option.dataset.brandName;
      const handle = option.dataset.brandHandle;

      this.selectedValue = name;
      this.setValue(name);
      this.close();

      // Redirect to PYD page with brand query param
      if (this.props.pydPageUrl && handle) {
        const separator = this.props.pydPageUrl.includes('?') ? '&' : '?';
        const redirectUrl = `${this.props.pydPageUrl}${separator}brand=${handle}`;
        window.location.href = redirectUrl;
        return;
      }
    });

    // Close when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) this.close();
    });

    // Close on Escape
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });

    this.addEventListener('focusout', () => {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
    });

    // Toggle dropdown on icon click
    this.icon?.addEventListener('click', () => {
      if (this.input?.hasAttribute('disabled')) return;
      if (this.isOpen) {
        this.close();
      } else {
        this.input.value = '';
        this.showAllBrands();
        this.open();
        this.input?.focus();
      }
    });
  }

  async loadBrands() {
    try {
      this.setLoading(true);

      // 1. Fetch brands from API
      const channels = this.props.channel || null;
      const brandResponse = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: `query Brands($channels: [ChannelName]) {
            brands(channels: $channels) {
              id
              name
            }
          }`,
          variables: channels ? { channels } : {}
        })
      });

      const brandData = await brandResponse.json();

      // 2. Safely extract brand list from API response and add handle property
      const brands = (brandData?.data?.brands || []).map(brand => ({
        ...brand,
        handle: this.handleize(brand.name)
      }));

      // 3. Define top brands to prioritize in the dropdown
      const topBrandNames = [
        'Apple', 'Samsung', 'Google', 'HTC', 'LG', 'Motorola', 'Nokia'
      ];

      // 4. Create a lookup map of all brands (case-insensitive)
      const brandMap = new Map(
        brands.map(b => [b.name.toLowerCase(), b])
      );

      // 5. Preserve top brand order while filtering out missing brands
      const topBrands = topBrandNames
        .map(name => brandMap.get(name.toLowerCase()))
        .filter(Boolean);

      // 6. Collect remaining brands as "Other"
      const otherBrands = brands.filter(
        b => !topBrandNames.some(name => b.name.toLowerCase() === name.toLowerCase())
      );

      // 7. Combine into grouped dropdown list with headers
      const groupedBrands = [
        { id: '', name: 'Top Brands', isHeader: true },
        ...topBrands,
        ...(otherBrands.length ? [{ id: '', name: 'Other Brands', isHeader: true }, ...otherBrands] : [])
      ];

      // 8. Store both grouped and flat brand lists
      this.brands = groupedBrands;
      this.filteredBrands = groupedBrands;
      this.renderBrands();
      this.enable();
    } catch (error) {
      console.error('Failed to load brands:', error);

      const toast = this.querySelector('gz-toast');
      if (toast) toast.show();
    } finally {
      this.setLoading(false);
    }
  }

  open() {
    if (this.input?.hasAttribute('disabled')) return;
    this.isOpen = true;
    this.dropdown?.removeAttribute('hidden');
    this.icon?.classList.add('is-open');
    this.input?.setAttribute('aria-expanded', 'true');
    this.icon?.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.isOpen = false;
    this.dropdown?.setAttribute('hidden', '');
    this.icon?.classList.remove('is-open');
    this.input?.setAttribute('aria-expanded', 'false');
    this.icon?.setAttribute('aria-expanded', 'false');
    this.setActiveIndex(-1);
  }

  // Every focusable brand option currently rendered (headers excluded).
  getSelectableOptions() {
    return Array.from(this.dropdown?.querySelectorAll('[role="option"]') || []);
  }

  setActiveIndex(index) {
    const options = this.getSelectableOptions();
    options.forEach((option) => {
      option.classList.remove('selected');
      option.setAttribute('aria-selected', 'false');
    });

    this.activeIndex = index;
    const activeOption = options[index];

    if (activeOption) {
      activeOption.classList.add('selected');
      activeOption.setAttribute('aria-selected', 'true');
      activeOption.scrollIntoView({ block: 'nearest' });
      this.input?.setAttribute('aria-activedescendant', activeOption.id);
    } else {
      this.input?.removeAttribute('aria-activedescendant');
    }
  }

  enable() {
    this.input?.removeAttribute('disabled');
  }

  disable() {
    this.input?.setAttribute('disabled', '');
    this.close();
  }

  setValue(label) {
    if (this.input) this.input.value = label;
  }

  setLoading(loading) {
    if (loading) {
      this.spinner?.removeAttribute('hidden');
      this.icon?.setAttribute('hidden', '');
    } else {
      this.spinner?.setAttribute('hidden', '');
      this.icon?.removeAttribute('hidden');
    }
  }

  showAllBrands() {
    this.filteredBrands = this.brands;
    this.renderBrands();
  }

  filter(query) {
    const q = query.trim().toLowerCase();
    this.filteredBrands = q
      ? this.brands.filter(brand =>
          !brand.isHeader &&
          brand.name.toLowerCase().includes(q)
        )
      : this.brands;
    this.renderBrands();
  }

  renderBrands() {
    if (!this.dropdown) return;

    const brands = this.filteredBrands.length
      ? this.filteredBrands
      : [{ name: 'No results', id: '', isHeader: true }];

    const selectableCount = brands.filter(brand => !brand.isHeader).length;

    let optionIndex = 0;
    this.dropdown.innerHTML = brands
      .map(brand => {
        if (brand.isHeader) {
          return `<div class="gz-hero-search__menu-header" aria-hidden="true">${brand.name}</div>`;
        }

        const { id, name, handle } = brand;
        const optionId = `${this.dropdown.id}-option-${optionIndex}`;
        optionIndex += 1;

        return `<div
          class="gz-hero-search__menu-item"
          id="${optionId}"
          role="option"
          aria-selected="false"
          aria-setsize="${selectableCount}"
          aria-posinset="${optionIndex}"
          data-brand-id="${id}"
          data-brand-name="${name}"
          data-brand-handle="${handle}"
        >
          ${name}
        </div>`;
      })
      .join('');

    this.setActiveIndex(-1);
    this.debouncedAnnounce();
  }

  announceResults() {
    if (!this.status) return;

    const count = this.getSelectableOptions().length;
    this.status.textContent = count
      ? `${count} brand${count === 1 ? '' : 's'} found`
      : 'No brands found';
  }

  // Converts a string into a URL-friendly "handle" or slug.
  handleize(input = '') {
    if (!input) return '';

    return input
      .trim()
      .toString()
      .toLowerCase()
      .normalize('NFD')                 // Split accented chars (é → e +  ́)
      .replace(/[\u0300-\u036f]/g, '')  // Remove diacritics
      .replace(/&/g, '-and-')           // Replace & with “and”
      .replace(/\+/g, '-plus-')         // Replace + with “plus”
      .replace(/[^a-z0-9]+/g, '-')      // Replace all non-alphanumeric with hyphen
      .replace(/^-+|-+$/g, '')          // Trim leading/trailing hyphens
      .replace(/-{2,}/g, '-');          // Collapse multiple hyphens
  }
}

customElements.define('gz-sell-search', GzSellSearch);